import React, { useEffect, useRef } from "react";
import { Heart } from "lucide-react";

const formatBpm = (v) => (v == null ? "—" : String(Math.round(v)));

/**
 * Live heart-rate tile for the equipment (ERG) dashboards. Mirrors the power
 * tiles' ref-painting trick: a direct BLE strap (`hr`) pushes packets straight
 * into the DOM node via `subscribe()`, so updates don't trigger React renders.
 * `getFreshSnapshot()` blanks the value when the strap goes silent (>3 s) so a
 * stale reading never lingers. Falls back to the `bpm` prop (ESP32-relayed
 * poll) when no direct strap is connected.
 */
const HrTile = ({ hr, bpm = null }) => {
  const nodeRef = useRef(null);
  // `hr` is rebuilt on every equipment-context bump, but its `subscribe` /
  // `getFreshSnapshot` are stable function refs from the session — depend on
  // those (not the wrapper object) so we don't resubscribe several times a
  // second. `bpm` is the ESP32 fallback and changes rarely (~3 s poll).
  const subscribe = hr?.subscribe;
  const getFreshSnapshot = hr?.getFreshSnapshot;

  useEffect(() => {
    const paint = () => {
      if (!nodeRef.current) return;
      const snap = getFreshSnapshot?.(3000);
      const v = snap?.bpm != null ? snap.bpm : bpm;
      nodeRef.current.textContent = formatBpm(v);
    };
    paint();
    const unsubscribe = subscribe?.(() => paint());
    return () => unsubscribe?.();
  }, [subscribe, getFreshSnapshot, bpm]);

  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3 flex flex-col items-center">
      <span
        ref={nodeRef}
        className="text-4xl font-mono font-bold tabular-nums text-red-400"
      >
        —
      </span>
      <span className="flex items-center gap-1 text-xs uppercase tracking-widest text-white/40 mt-1">
        <Heart className="w-3 h-3 text-red-500" />
        HR · bpm
      </span>
    </div>
  );
};

export default HrTile;
